import React, {useEffect, useRef} from 'react'
import AdyenCheckout from '@adyen/adyen-web'
import '@adyen/adyen-web/dist/adyen.css'
import {Flex, Spinner} from '@chakra-ui/react'
import PropTypes from 'prop-types'
import {getCurrencyValueForApi} from '../utils/parsers.js'
import useAdyenExpressCheckout from '../hooks/useAdyenExpressCheckout.js'
import {Box} from '@salesforce/retail-react-app/app/components/shared/ui/index.jsx'
import {AdyenPaymentsService} from '../services/payments.js'
import {AdyenPaymentsDetailsService} from '../services/payments-details.js'
import {AdyenShippingAddressService} from '../services/shipping-address.js'
import {AdyenHandleExpressPaymentService} from '../services/handle-express-payment.js'
import {AdyenShippingMethodsService} from '../services/shipping-methods.js'
import CustomApi from '../../../hooks/use-custom-api.js'
import {useCommerceApi} from '@salesforce/commerce-sdk-react/index.js'
import {useStoreContext} from '../../../contexts/store.jsx'
import useAdyenCheckout from '../hooks/useAdyenCheckout.js'
import {formatNames} from '../../../api/utils/formatAddress.js'
import useTagManager from '../../../hooks/use-tag-manager.js'
import {isFineJewelryExists} from '../../../utils/basket-utils.js'
import {TWO_DAY_METHOD_ID} from '../../../constants.js'

export const getPayPalPaymentMethodConfig = (paymentMethodsResponse) => {
    const payPalPaymentMethod = paymentMethodsResponse?.paymentMethods?.find(
        (pm) => pm.type === 'paypal'
    )
    return payPalPaymentMethod?.configuration || null
}

const formatPayPalDeliveryAddress = (address) => {
    if (!address) {
        return null
    }

    return {
        city: address.city || '',
        country: address.country || address.countryCode || '',
        postalCode: address.postalCode || '',
        stateOrProvince: address.stateOrProvince || address.state || '',
        street: address.street || ''
    }
}

function formatPayPalShippingMethods(applicableShippingMethods, basket, selectedMethodId) {
    return applicableShippingMethods.map(function (shippingMethod) {
        var isSelected = shippingMethod.id === (selectedMethodId || 'Free')
        var value = shippingMethod.price
        const shippingTotal = basket.shipments?.[0].shippingTotal // We'll respect the promotions applied to the Shipping Methods
        return {
            reference: shippingMethod.id,
            description: shippingMethod.name,
            type: shippingMethod?.c_storePickupEnabled ? 'PICKUP' : 'SHIPPING',
            amount: {
                value: getCurrencyValueForApi(isSelected ? shippingTotal : value, basket.currency),
                currency: basket.currency
            },
            selected: isSelected
        }
    })
}

const formatPayPalShopperDetails = (payer, deliveryAddress, billingAddress) => {
    const result = {
        deliveryAddress: formatPayPalDeliveryAddress(deliveryAddress),
        profile: {
            email: payer?.email_address || '',
            firstName: formatNames(payer?.name?.given_name || ''),
            lastName: formatNames(payer?.name?.surname || ''),
            phone: payer?.phone?.phone_number?.national_number || ''
        }
    }

    if (billingAddress) {
        result.billingAddress = formatPayPalDeliveryAddress(billingAddress)
    }

    return result
}

export const getPayPalButtonConfig = (
    authToken,
    site,
    basket,
    shippingMethods,
    payPalPaymentMethodConfig,
    navigate,
    fetchShippingMethods,
    api,
    customerId,
    setShowPaypalError,
    isBopis = false,
    store
) => {
    const isBopisSelected = Boolean(
        isBopis ||
            basket?.shipments?.some((shipment) => shipment?.shippingMethod?.c_storePickupEnabled)
    )
    const customapi = new CustomApi()
    const {proxy} = api.shopperExperience.clientConfig
    const totalGiftCardAmount =
        basket?.giftCertificateItems?.reduce((sum, {amount = 0}) => sum + amount, 0) || 0
    const ppAmount = {
        value: getCurrencyValueForApi(basket?.productTotal + totalGiftCardAmount, basket.currency), // Please send product (and gift card items) total here, we'll update this in 'paypal-update-order' API with correct tax, shipping cost and order total.
        currency: basket.currency
    }
    const validMethods = shippingMethods.filter(
        (m) => !m.c_isGeShippingMethod && !m.c_hideFromStorefront && typeof m.price === 'number'
    )
    const ppDeliveryMethods = validMethods?.map((sm) => ({
        description: sm.description,
        reference: sm.id,
        type: sm?.c_storePickupEnabled ? 'PICKUP' : 'SHIPPING',
        amount: {
            value: getCurrencyValueForApi(sm.price, basket.currency),
            currency: basket.currency
        },
        selected: sm.id === (basket?.shipments?.[0].shippingMethod?.id || 'Free')
    }))

    const paidWithGC = (basket) => {
        const giftCertificates = basket?.paymentInstruments?.filter(
            (pi) => pi.paymentMethodId === 'GIFT_CERTIFICATE'
        )

        let paidWithGiftCard = 0

        giftCertificates?.forEach((gc) => {
            paidWithGiftCard += gc?.amount
        })

        return Number(paidWithGiftCard.toFixed(2))
    }

    const storeDeliveryAddress =
        isBopisSelected && store
            ? {
                  street: store.address1 || '',
                  city: store.city || '',
                  country: store.countryCode || '',
                  postalCode: store.postalCode || '',
                  stateOrProvince: store.state || ''
              }
            : undefined

    return {
        showPayButton: true,
        isExpress: true,
        configuration: payPalPaymentMethodConfig,
        // Pre-fill store address when in-store pickup
        ...(isBopisSelected &&
            storeDeliveryAddress && {
                shopperDetails: {
                    deliveryAddress: storeDeliveryAddress
                }
            }),
        onSubmit: async function (state, component) {
            setShowPaypalError(false) // make sure to reset error state on new submission
            try {
                const adyenPaymentService = new AdyenPaymentsService(authToken, site)
                const paymentsResponse = await adyenPaymentService.submitPayment(
                    {
                        ...state.data,
                        origin: state.data.origin ? state.data.origin : window.location.origin,
                        paymentType: 'express',
                        orderNo: basket?.c_orderNo
                    },

                    basket?.basketId,
                    customerId,
                    true
                )

                if (paymentsResponse.action) {
                    component.handleAction(paymentsResponse.action)
                }
            } catch (e) {
                console.warn('Error occurred in onSubmit for PayPal', e.message)
                setShowPaypalError(true)
            }
        },
        onAdditionalDetails: async function (state, actions) {
            const adyenPaymentsDetailsService = new AdyenPaymentsDetailsService(authToken, site)
            try {
                const paymentType = 'express'
                const data = {
                    ...state.data,
                    paymentType
                }
                const paymentsDetailsResponse =
                    await adyenPaymentsDetailsService.submitPaymentsDetails(
                        data,
                        basket?.basketId,
                        basket?.customerInfo?.customerId
                    )
                if (paymentsDetailsResponse?.isSuccessful) {
                    actions.resolve()
                    navigate(`/checkout/confirmation/${paymentsDetailsResponse?.merchantReference}`)
                }
            } catch (e) {
                console.warn('Error occurred in onAdditionalDetails for PayPal', e.message)
                actions.reject()
                setShowPaypalError(true)
            }
        },
        onError: function (error, actions) {
            console.warn('Error catched in onError', error)
            actions.reject()
        },
        countryCode: 'US', // Hardcode
        amount: ppAmount,
        deliveryMethods: isBopisSelected ? [] : ppDeliveryMethods,
        onShopperDetails: async function (shopperDetails, rawData, actions) {
            setShowPaypalError(false) // clear any error raised while the PayPal modal was open
            const {shopperEmail, billingAddress, shippingAddress, shopperName} =
                shopperDetails || {}
            const {purchase_units} = rawData || {}
            const shipping = purchase_units?.[0]?.shipping?.name?.full_name
            try {
                const [shippingFirstName, ...rest] = (shipping || '').trim().split(/\s+/)
                const shippingLastName = rest.join(' ')
                const addressFirstName = formatNames(
                    shippingFirstName || shopperName.firstName || 'Customer'
                )
                const addressLastName = formatNames(shippingLastName || shopperName.lastName || '.')
                const isBopis = isBopisSelected || window.paypalExpressIsBopis
                const data = {
                    deliveryAddress: {
                        street: isBopis && store ? store.address1 : shippingAddress.street,
                        city: isBopis && store ? store.city : shippingAddress.city,
                        country: isBopis && store ? store.countryCode : shippingAddress.country,
                        postalCode:
                            isBopis && store ? store.postalCode : shippingAddress.postalCode,
                        stateOrProvince:
                            isBopis && store ? store.stateCode : shippingAddress.stateOrProvince,
                        firstName: isBopis && store ? store.name : addressFirstName,
                        lastName: isBopis && store ? '' : addressLastName,
                        fullName:
                            isBopis && store
                                ? store.name
                                : shipping || `${addressFirstName} ${addressLastName}`
                    },
                    billingAddress: {
                        street: billingAddress.street,
                        city: billingAddress.city,
                        country: billingAddress.country,
                        postalCode: billingAddress.postalCode,
                        stateOrProvince: billingAddress.stateOrProvince
                    },
                    profile: {
                        email: shopperEmail,
                        firstName: formatNames(shopperName.firstName),
                        lastName: formatNames(shopperName.lastName)
                    },
                    isBopis: isBopisSelected && !!store
                }
                const adyenExpressPayment = new AdyenHandleExpressPaymentService(authToken, site)
                await adyenExpressPayment.handlExpressPayment(data, basket?.basketId, customerId)
                actions.resolve()
            } catch (e) {
                console.warn('Error occurred in onShopperDetails for PayPal', e.message)
                setShowPaypalError(true)
                actions.reject()
            }
        },
        onShippingAddressChange: async function (data, actions, component) {
            const adyenShippingAddressService = new AdyenShippingAddressService(authToken, site)
            const adyenShippingMethodsService = new AdyenShippingMethodsService(authToken, site)
            const {shippingAddress} = data
            const currentPaymentData = component.paymentData
            const customerShippingDetails = formatPayPalShopperDetails(null, shippingAddress)
            const isBopis = isBopisSelected || window.paypalExpressIsBopis
            let basketResponse = basket
            try {
                if (!isBopis) {
                    basketResponse = await adyenShippingAddressService.updateShippingAddress(
                        basket.basketId,
                        customerShippingDetails
                    )
                }

                const {applicableShippingMethods} = await fetchShippingMethods(
                    basket?.basketId,
                    site,
                    authToken,
                    isBopis
                )

                if (!applicableShippingMethods?.length) {
                    return actions.reject(data.errors.ADDRESS_ERROR)
                }

                let applicableShippingMethod = isBopis
                    ? applicableShippingMethods?.find(
                          ({c_storePickupEnabled}) => c_storePickupEnabled
                      )
                    : applicableShippingMethods?.find(
                          ({id}) => id === basketResponse?.shipments?.[0]?.shippingMethod?.id
                      )
                applicableShippingMethod = applicableShippingMethod || applicableShippingMethods[0]

                /** Select Two Day method if it need **/
                let forceMethodId = ''
                const isTwoDayApplicable = Boolean(
                    applicableShippingMethods?.some((method) => method.id === TWO_DAY_METHOD_ID)
                )
                const fineJewelryExists = isFineJewelryExists(window.pwaProducts)
                if (
                    localStorage.getItem('customerSelectedShippingMethod') !== 'true' &&
                    !localStorage.getItem('2DayApplied') &&
                    fineJewelryExists &&
                    !isBopis &&
                    // Only force Two-Day where it can actually ship; on APO/FPO/US-territory
                    // addresses it isn't applicable and the shipment should default to Overseas.
                    isTwoDayApplicable
                ) {
                    forceMethodId = TWO_DAY_METHOD_ID
                    localStorage.removeItem('customerSelectedShippingMethod')
                    localStorage.setItem('2DayApplied', true)
                }
                /** Select Two Day method if it need **/

                if (
                    applicableShippingMethod.id !== basketResponse.shipments[0].shippingMethod.id ||
                    forceMethodId
                ) {
                    basketResponse = await adyenShippingMethodsService.updateShippingMethod(
                        forceMethodId || applicableShippingMethod.id,
                        basketResponse.basketId
                    )
                }

                const ppMethods = formatPayPalShippingMethods(
                    applicableShippingMethods,
                    basketResponse,
                    forceMethodId || applicableShippingMethod.id
                )
                const paidWithGiftCard = paidWithGC(basketResponse)
                const body = {
                    applicableShippingMethods: ppMethods.length ? ppMethods : [],
                    currentPaymentData: currentPaymentData,
                    amount: {
                        value: getCurrencyValueForApi(
                            basketResponse.orderTotal - paidWithGiftCard,
                            basketResponse.currency
                        ),
                        currency: basketResponse.currency
                    },
                    taxTotal: {
                        value: getCurrencyValueForApi(
                            basketResponse.taxTotal,
                            basketResponse.currency
                        ),
                        currency: basketResponse.currency
                    }
                }

                const response = await customapi.post(
                    authToken,
                    'paypal-update-order',
                    '/',
                    {},
                    proxy,
                    body
                )
                if (response?.status === 'success') {
                    component.updatePaymentData(response?.paymentData)
                } else {
                    console.warn(
                        'Error occurred in custom API paypal-update-order for PayPal',
                        response
                    )
                    // Modal-time failure: let PayPal surface it in its own UI, the order can
                    // still be placed, so do not raise the terminal checkout error.
                    actions.reject(data.errors.ADDRESS_ERROR)
                }
            } catch (e) {
                console.warn('Error occurred in onShippingAddressChange for PayPal', e.message)
                actions.reject(data.errors.ADDRESS_ERROR)
            }
        },
        onShippingOptionsChange: async function (data, actions, component) {
            const adyenShippingMethodsService = new AdyenShippingMethodsService(authToken, site)
            const {selectedShippingOption} = data
            const currentPaymentData = component.paymentData
            const isBopis = isBopisSelected || window.paypalExpressIsBopis
            try {
                const {applicableShippingMethods} = await fetchShippingMethods(
                    basket?.basketId,
                    site,
                    authToken,
                    isBopis
                )

                const applicableShippingMethod = applicableShippingMethods?.find(
                    ({id}) => id === selectedShippingOption.id
                )

                if (!applicableShippingMethod) {
                    return actions.reject(data.errors.METHOD_UNAVAILABLE)
                }

                const basketResponse = await adyenShippingMethodsService.updateShippingMethod(
                    applicableShippingMethod.id,
                    basket.basketId
                )

                const ppMethods = formatPayPalShippingMethods(
                    applicableShippingMethods,
                    basketResponse,
                    applicableShippingMethod.id
                )
                const paidWithGiftCard = paidWithGC(basketResponse)
                const body = {
                    applicableShippingMethods: ppMethods.length ? ppMethods : [],
                    currentPaymentData: currentPaymentData,
                    amount: {
                        value: getCurrencyValueForApi(
                            basketResponse.orderTotal - paidWithGiftCard,
                            basketResponse.currency
                        ),
                        currency: basketResponse.currency
                    },
                    taxTotal: {
                        value: getCurrencyValueForApi(
                            basketResponse.taxTotal,
                            basketResponse.currency
                        ),
                        currency: basketResponse.currency
                    }
                }

                const response = await customapi.post(
                    authToken,
                    'paypal-update-order',
                    '/',
                    {},
                    proxy,
                    body
                )
                if (response?.status === 'success') {
                    component.updatePaymentData(response?.paymentData)
                } else {
                    console.warn(
                        'Error occurred in custom API paypal-update-order for PayPal',
                        response
                    )
                    actions.reject(data.errors.METHOD_UNAVAILABLE)
                }
            } catch (e) {
                console.warn('Error occurred in onShippingOptionsChange for PayPal', e.message)
                actions.reject(data.errors.METHOD_UNAVAILABLE)
            }
        },
        blockPayPalVenmoButton: true,
        blockPayPalCreditButton: true,
        blockPayPalPayLaterButton: true
    }
}

/* -----------------------------
   Component
------------------------------ */

const PayPalExpress = (props) => {
    const {adyenEnvironment, adyenPaymentMethods, shippingMethods, fetchShippingMethods} =
        props.isCheckout ? useAdyenCheckout() : useAdyenExpressCheckout() // eslint-disable-line react-hooks/rules-of-hooks
    const api = useCommerceApi()
    const {store} = useStoreContext()
    const paymentContainer = useRef(null)
    const paypalButtonRef = useRef(null) // store instance
    const tagManager = useTagManager()
    const {
        basket,
        authToken,
        customerId,
        locale,
        site,
        navigate,
        setShowPaypalError,
        isBopis = false,
        isCheckout
    } = props

    useEffect(() => {
        let isActive = true
        // Make 'window.paypal' undefined if it is 'svg', so our rendering of the PayPal button will not be disturbed by the 'svg'
        if (window?.paypal?.firstElementChild) {
            window.paypal = undefined
        }
        // We do not actually remount PayPal express button when BOPIS is selected in cart
        // but onShippingAddressChange needs to know current status
        window.paypalExpressIsBopis = isBopis

        const createCheckout = async () => {
            try {
                const checkout = await AdyenCheckout({
                    environment: adyenEnvironment?.ADYEN_ENVIRONMENT,
                    clientKey: adyenEnvironment?.ADYEN_CLIENT_KEY,
                    countryCode: 'US',
                    locale: locale.id,
                    paymentMethodsResponse: adyenPaymentMethods,
                    analytics: {
                        analyticsData: {
                            applicationInfo: adyenPaymentMethods?.applicationInfo
                        }
                    },
                    paymentMethodsConfiguration: {
                        paypal: {
                            style: {
                                layout: 'vertical',
                                color: 'gold',
                                disableMaxWidth: true,
                                shape: 'rect',
                                label: 'paypal',
                                height: 50
                            },
                            onClick: () => {
                                if (!isCheckout) {
                                    tagManager.paymentMethodSelection('paypal_express')
                                }
                            }
                        }
                    }
                })

                const payPalPaymentMethodConfig = getPayPalPaymentMethodConfig(adyenPaymentMethods)

                const paypalButtonConfig = getPayPalButtonConfig(
                    authToken,
                    site,
                    basket,
                    shippingMethods?.applicableShippingMethods,
                    payPalPaymentMethodConfig,
                    navigate,
                    fetchShippingMethods,
                    api,
                    customerId,
                    setShowPaypalError,
                    isBopis,
                    store,
                    isCheckout
                )
                const paypalButton = checkout.create('paypal', paypalButtonConfig)
                if (
                    isActive &&
                    paypalButton &&
                    paymentContainer.current &&
                    !paypalButtonRef.current
                ) {
                    paypalButton.mount(paymentContainer.current)
                    paypalButtonRef.current = paypalButton
                }
            } catch (err) {
                console.warn('Adyen PayPal error:', err)
            }
        }

        if (
            adyenEnvironment &&
            adyenPaymentMethods &&
            basket &&
            shippingMethods &&
            paymentContainer.current &&
            !paypalButtonRef.current // prevent duplicate mounts
        ) {
            createCheckout()
        }
        // cleanup: unmount old instance before next run
        return () => {
            isActive = false
        }
    }, [adyenEnvironment, adyenPaymentMethods, basket, shippingMethods, isBopis])

    return (
        <Box className="paypal-container-wrapper">
            {props.showLoading && (
                <Flex align="center" justify="center">
                    <Spinner size="lg" mt={4} />
                </Flex>
            )}
            <div ref={paymentContainer}></div>
            <Box display="none" className="paypal-message-container"></Box>
        </Box>
    )
}

PayPalExpress.propTypes = {
    basket: PropTypes.any,
    authToken: PropTypes.any,
    customerId: PropTypes.any,
    locale: PropTypes.any,
    site: PropTypes.any,
    showLoading: PropTypes.bool,
    setShowPaypalError: PropTypes.func,
    isBopis: PropTypes.bool,
    navigate: PropTypes.any,
    isCheckout: PropTypes.bool
}

export default PayPalExpress
